'use client';

import type { ReactElement } from 'react';

import clsx from 'clsx';
import Image, { ImageLoader, ImageLoaderProps } from 'next/image';
import { useEmbeddableSearchProviderContext } from '@provider-directory/embeddable/embeddable-search.context';
import type { ProviderResource } from '@provider-directory/types/providers.contracts';

import './provider-image.css';

export const ProviderImage = ({
  testIdPrefix,
  name,
  imageUrl,
  className,
  priority,
  fill,
  width,
  height,
  quality,
}: {
  name: ProviderResource['name'];
  imageUrl?: string;
  className?: string;
  priority?: boolean;
  fill?: boolean;
  width?: number;
  height?: number;
  testIdPrefix?: string;
  quality?: number;
}): ReactElement => {
  const firstInitial = name.first.charAt(0);
  const lastInitial = name.last.charAt(0);
  const embeddableContext = useEmbeddableSearchProviderContext();

  // When running in an embeddable context, we need to use a custom loader to ensure
  // the image is loaded from the actual app domain and not from the host domain
  const customLoader: ImageLoader | undefined = embeddableContext?.directoryUrl
    ? ({ src, width: widthParam, quality: qualityParam }: ImageLoaderProps) => {
        const params = new URLSearchParams();
        params.set('url', src);
        params.set('w', widthParam.toString());
        if (qualityParam !== undefined) {
          params.set('q', qualityParam.toString());
        }

        return new URL(
          `/_next/image?${params.toString()}`,
          embeddableContext.directoryUrl,
        ).href;
      }
    : undefined;

  return imageUrl ? (
    <Image
      className={clsx(['provider-image rounded-lg object-cover', className])}
      src={imageUrl}
      alt={`Photo of ${name.full}`}
      width={width}
      height={height}
      fill={fill}
      priority={priority}
      data-testid={testIdPrefix ? `${testIdPrefix}-image` : undefined}
      quality={quality || 75}
      loader={customLoader}
    />
  ) : (
    <div
      data-testid={testIdPrefix ? `${testIdPrefix}-placeholder` : undefined}
      className={clsx([
        'flex items-center justify-center rounded-lg bg-primary-100 text-body-xlarge text-primary-600',
        className,
      ])}
    >
      {`${firstInitial}${lastInitial}`}
    </div>
  );
};
